import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Layers, ArrowRight, RefreshCw, FolderOpen, LayoutGrid, ArrowUpCircle, Banknote, BarChart3, CalendarRange } from "lucide-react";
import { useGetActiveUserCategoriesQuery } from "../../application/api/probationerApplicationApi";
import { useAdminGradeUpgradesQuery } from "../api/upgradeApi";
import RecordBankPaymentModal from "../components/RecordBankPaymentModal";
import UpgradeAnalysisModal from "../components/UpgradeAnalysisModal";

/** Grades a member can upgrade INTO (the upgrade "categories"). */
const TARGET_GRADES = ["probationer", "member", "fellow"];

function CategoryCard({
  id,
  name,
  count,
}: {
  id: number;
  name: string;
  count: number;
}) {
  return (
    <Link
      to={`/admin/upgrade-applications/category/${id}`}
      state={{ categoryName: name }}
      className="group relative bg-white border border-gray-200 rounded-[32px] p-8 transition-all hover:shadow-2xl hover:shadow-gray-200 hover:-translate-y-1 overflow-hidden"
    >
      <div className="absolute top-0 right-0 w-24 h-24 bg-gray-50 rounded-bl-[60px] -mr-8 -mt-8 transition-colors group-hover:bg-indigo-50" />
      <div className="relative z-10 flex flex-col h-full">
        <div className="w-12 h-12 bg-gray-100 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-indigo-600 group-hover:text-white transition-all shadow-sm">
          <FolderOpen className="w-6 h-6" />
        </div>
        <div className="flex items-center gap-2 mb-2">
          <h3 className="text-xl font-black text-gray-900 tracking-tight group-hover:text-indigo-600 transition-colors">
            Upgrades to {name}
          </h3>
          <span className="px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 text-xs font-bold border border-indigo-100">
            {count}
          </span>
        </div>
        <p className="text-sm font-medium text-gray-500 leading-relaxed mb-6">
          Review and decide on members applying to become {name}.
        </p>
        <div className="mt-auto pt-6 border-t border-gray-50 flex items-center justify-between">
          <span className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 group-hover:text-gray-900 transition-colors">
            Enter Category
          </span>
          <div className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center group-hover:bg-indigo-600 group-hover:text-white transition-all">
            <ArrowRight className="w-4 h-4" />
          </div>
        </div>
      </div>
    </Link>
  );
}

export default function AdminUpgradeApplicationsPage() {
  const { data: categories = [], isLoading: catLoading, refetch: refetchCats } =
    useGetActiveUserCategoriesQuery();
  const { data, isFetching, refetch: refetchApps } = useAdminGradeUpgradesQuery({ per_page: 500 });

  const rows: any[] = useMemo(
    () => (Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : []),
    [data]
  );

  // Count applications per target category.
  const countByCategory = useMemo(() => {
    const m = new Map<number, number>();
    for (const r of rows) {
      const id = r?.to_category?.id ?? r?.to_category_id;
      if (id != null) m.set(Number(id), (m.get(Number(id)) || 0) + 1);
    }
    return m;
  }, [rows]);

  const targets = useMemo(
    () =>
      (Array.isArray(categories) ? categories : []).filter((c: any) =>
        TARGET_GRADES.includes(String(c.name).toLowerCase())
      ),
    [categories]
  );

  const [showBankModal, setShowBankModal] = useState(false);
  const [showAnalysis, setShowAnalysis] = useState(false);

  const refetch = () => {
    refetchCats();
    refetchApps();
  };

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-8 space-y-6">
      {/* Title row */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 bg-white border border-gray-100 p-6 rounded-[32px] shadow-sm">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 bg-indigo-50 border border-indigo-100 rounded-2xl flex items-center justify-center">
            <ArrowUpCircle className="w-5 h-5 text-indigo-600" />
          </div>
          <div className="space-y-1">
            <h1 className="text-3xl font-black text-gray-900 tracking-tight">Grade Upgrades</h1>
            <p className="text-sm font-bold text-gray-500">
              Choose a category to review its upgrade applications.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 self-start">
          {/* Committee review runs in cycles, like logbook assessment diets. */}
          <Link
            to="/admin/upgrade-applications/review-cycles"
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 text-gray-700 text-sm font-bold rounded-xl hover:bg-gray-50 transition-all shadow-sm"
          >
            <CalendarRange className="w-4 h-4" />
            Review Cycles
          </Link>
          <button
            onClick={() => setShowAnalysis(true)}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 text-gray-700 text-sm font-bold rounded-xl hover:bg-gray-50 transition-all shadow-sm"
          >
            <BarChart3 className="w-4 h-4" />
            Analysis
          </button>
          <button
            onClick={() => setShowBankModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-indigo-600 text-white text-sm font-bold rounded-xl hover:bg-indigo-700 transition-all shadow-sm"
          >
            <Banknote className="w-4 h-4" />
            Record Bank Payment
          </button>
          <button
            onClick={refetch}
            disabled={isFetching || catLoading}
            className="p-2.5 bg-white border border-gray-100 rounded-xl hover:bg-gray-50 transition-all shadow-sm"
          >
            <RefreshCw className={`w-4 h-4 text-gray-500 ${isFetching ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* Category grid */}
      {catLoading ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-64 bg-gray-50 border border-gray-100 rounded-[32px] animate-pulse" />
          ))}
        </div>
      ) : targets.length === 0 ? (
        <div className="py-20 bg-gray-50 border-2 border-dashed border-gray-200 rounded-[40px] flex flex-col items-center justify-center text-center">
          <div className="w-16 h-16 bg-white rounded-3xl flex items-center justify-center shadow-sm mb-4">
            <LayoutGrid className="w-8 h-8 text-gray-300" />
          </div>
          <h2 className="text-xl font-black text-gray-900 uppercase">No categories</h2>
          <p className="text-gray-500 max-w-xs mx-auto mt-2 font-medium">No upgrade categories found.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {targets.map((c: any) => (
            <CategoryCard key={c.id} id={c.id} name={c.name} count={countByCategory.get(Number(c.id)) || 0} />
          ))}
        </div>
      )}

      {/* Info footer */}
      <div className="mt-6 p-8 bg-indigo-50/50 rounded-[32px] border border-indigo-100 flex items-start gap-4">
        <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-sm shrink-0">
          <Layers className="text-indigo-600 w-5 h-5" />
        </div>
        <div>
          <h4 className="text-xs font-black text-indigo-900 uppercase tracking-widest mb-1">Administrative Note</h4>
          <p className="text-xs font-bold text-indigo-700/70 leading-relaxed max-w-2xl">
            Upgrade applications are grouped by the grade a member is applying to attain
            (Student → Probationer → Member → Fellow). Open a category to filter, search and review its submissions.
          </p>
        </div>
      </div>

      <RecordBankPaymentModal
        open={showBankModal}
        onClose={() => setShowBankModal(false)}
        onDone={refetch}
      />

      {/* Mounted only while open, so the aggregate query runs on demand rather
          than on every page load. */}
      {showAnalysis && <UpgradeAnalysisModal onClose={() => setShowAnalysis(false)} />}
    </div>
  );
}
